// Belt run goals — port of v1 js/goal-manager.js. One challenge at a time,
// completed purely by playing; each full pass doubles targets and raises
// bonuses. Wired to the event bus instead of direct calls.

import type { EventBus } from '@/app/events';
import type { Lang } from '@/ui/i18n';

type GoalType =
  | 'coins'
  | 'nearMiss'
  | 'flips'
  | 'jumpEnemy'
  | 'combo'
  | 'roofRide'
  | 'construction'
  | 'combatWin'
  | 'distance';

type LabelFn = (n: number) => string;

const GOAL_TYPES: Record<GoalType, { en: LabelFn; de: LabelFn }> = {
  coins: { en: (n) => `Collect ${n} coins`, de: (n) => `Sammle ${n} Münzen` },
  nearMiss: { en: (n) => `Dodge ${n} cars closely`, de: (n) => `Weiche ${n} Autos knapp aus` },
  flips: { en: (n) => `Do ${n} mid-air flips`, de: (n) => `Mache ${n} Saltos` },
  jumpEnemy: {
    en: (n) => (n === 1 ? 'Jump over an enemy' : `Jump over ${n} enemies`),
    de: (n) => (n === 1 ? 'Spring über einen Gegner' : `Spring über ${n} Gegner`),
  },
  combo: { en: (n) => `Reach a ${n} combo`, de: (n) => `Erreiche eine ${n}er-Combo` },
  roofRide: {
    en: (n) => (n === 1 ? 'Land on a car roof' : `Land on ${n} car roofs`),
    de: (n) => (n === 1 ? 'Lande auf einem Autodach' : `Lande auf ${n} Autodächern`),
  },
  construction: {
    en: (n) => (n === 1 ? 'Clear a construction site' : `Clear ${n} construction sites`),
    de: (n) => (n === 1 ? 'Überspringe eine Baustelle' : `Überspringe ${n} Baustellen`),
  },
  combatWin: {
    en: (n) => (n === 1 ? 'Win a street fight' : `Win ${n} street fights`),
    de: (n) => (n === 1 ? 'Gewinne einen Straßenkampf' : `Gewinne ${n} Straßenkämpfe`),
  },
  distance: { en: (n) => `Run ${n}m`, de: (n) => `Laufe ${n}m` },
};

// v1 GOAL_SEQUENCE verbatim
const GOAL_SEQUENCE: readonly { type: GoalType; target: number; bonus: number }[] = [
  { type: 'coins', target: 10, bonus: 50 },
  { type: 'nearMiss', target: 3, bonus: 75 },
  { type: 'flips', target: 3, bonus: 75 },
  { type: 'jumpEnemy', target: 2, bonus: 100 },
  { type: 'combo', target: 10, bonus: 100 },
  { type: 'roofRide', target: 1, bonus: 125 },
  { type: 'coins', target: 25, bonus: 125 },
  { type: 'construction', target: 2, bonus: 150 },
  { type: 'distance', target: 500, bonus: 150 },
  { type: 'combatWin', target: 1, bonus: 150 },
  { type: 'nearMiss', target: 8, bonus: 200 },
];

const NEXT_GOAL_DELAY = 2.5;

interface ActiveGoal {
  type: GoalType;
  target: number;
  bonus: number;
}

export class GoalSystem {
  completedCount = 0;
  onComplete: ((goal: ActiveGoal) => void) | null = null;
  onLabelChange: ((label: string) => void) | null = null;

  private sequenceIndex = 0;
  private loop = 0;
  private activeGoal: ActiveGoal | null = null;
  private progress = 0;
  private nextGoalTimer = 0;
  private distanceAtActivation = 0;
  private lastDistance = 0;

  constructor(
    bus: EventBus,
    private readonly lang: Lang,
  ) {
    bus.on('coin:collected', () => this.onEvent('coins'));
    bus.on('player:near-miss', () => this.onEvent('nearMiss'));
    bus.on('player:jumped', ({ double }) => {
      if (double) this.onEvent('flips');
    });
    bus.on('enemy:jumped', () => this.onEvent('jumpEnemy'));
    bus.on('player:roof-ride', () => this.onEvent('roofRide'));
    bus.on('construction:cleared', () => this.onEvent('construction'));
    bus.on('combat:ended', ({ victory }) => {
      if (victory) this.onEvent('combatWin');
    });
    bus.on('combo:changed', ({ combo }) => this.reportCombo(combo));
  }

  reset(): void {
    this.sequenceIndex = 0;
    this.loop = 0;
    this.activeGoal = null;
    this.progress = 0;
    this.completedCount = 0;
    this.nextGoalTimer = 0;
    this.lastDistance = 0;
    this.activateNext();
  }

  private activateNext(): void {
    const template = GOAL_SEQUENCE[this.sequenceIndex];
    if (!template) return;
    const scale = Math.pow(2, this.loop); // Each pass doubles targets (v1 :90)
    this.activeGoal = {
      type: template.type,
      target: template.target * scale,
      bonus: template.bonus + this.loop * 100,
    };
    this.progress = 0;
    this.distanceAtActivation = this.lastDistance;

    this.sequenceIndex++;
    if (this.sequenceIndex >= GOAL_SEQUENCE.length) {
      this.sequenceIndex = 0;
      this.loop++;
    }
    this.updateLabel();
  }

  private onEvent(type: GoalType, amount = 1): void {
    if (!this.activeGoal || this.activeGoal.type !== type) return;
    this.progress += amount;
    this.checkCompletion();
  }

  private reportCombo(combo: number): void {
    if (!this.activeGoal || this.activeGoal.type !== 'combo') return;
    if (combo > this.progress) {
      this.progress = combo;
      this.checkCompletion();
    }
  }

  reportDistance(totalDistance: number): void {
    this.lastDistance = totalDistance;
    if (!this.activeGoal || this.activeGoal.type !== 'distance') return;
    const ran = totalDistance - this.distanceAtActivation;
    if (ran > this.progress) {
      this.progress = ran;
      this.checkCompletion();
    }
  }

  private checkCompletion(): void {
    if (!this.activeGoal) return;
    if (this.progress < this.activeGoal.target) {
      this.updateLabel();
      return;
    }
    const completed = this.activeGoal;
    this.activeGoal = null;
    this.completedCount++;
    this.nextGoalTimer = NEXT_GOAL_DELAY;
    this.onLabelChange?.(
      this.lang === 'de' ? '✅ Herausforderung geschafft!' : '✅ Challenge complete!',
    );
    this.onComplete?.(completed);
  }

  update(dt: number): void {
    if (this.activeGoal || this.nextGoalTimer <= 0) return;
    this.nextGoalTimer -= dt;
    if (this.nextGoalTimer <= 0) this.activateNext();
  }

  hide(): void {
    this.onLabelChange?.('');
  }

  private updateLabel(): void {
    if (!this.activeGoal) return;
    const texts = GOAL_TYPES[this.activeGoal.type];
    const label = (this.lang === 'de' ? texts.de : texts.en)(this.activeGoal.target);
    const shown = Math.min(Math.floor(this.progress), this.activeGoal.target);
    this.onLabelChange?.(`🥋 ${label} (${shown}/${this.activeGoal.target})`);
  }
}
